<script setup lang="ts">
import { computed } from 'vue'
import useProductSharing from '@/legacy/ts/useProductSharing'
import {
	getProductSharingUrl,
	productSharingData,
} from '@/legacy/ts/productSharing'
import BsButtonNormal from '@/legacy/components/ButtonNormal.vue'
import BsButtonOutline from '@/legacy/components/ButtonOutline.vue'
import injectStrict from '@/lib/inject'
import { instanceProductSharing } from '@/assets/ts/apiManagement'
import { getVueAssetImagePath } from '@/lib/utils'

const { isHasPSProfile } = useProductSharing()
const props = defineProps({
	userInfo: {
		type: Object,
		default: null,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	text: {
		type: Object,
	},
	selfProfileName: {
		type: String,
		default: '',
	},
})
const addMessage: (message: string) => void = injectStrict('addMessage')
const emit = defineEmits(['update'])

const userAvatar = computed(() => {
	if (!props.userInfo || !props.userInfo.image) {
		return getVueAssetImagePath('avatar_2_48px@2x.png')
	}
	return props.userInfo.image.content
})

const isNotSelf = computed(() => {
	return props.userInfo.username !== props.selfProfileName
})

function updateProfileFollowFlag() {
	if (!productSharingData.isLogin) {
		window.BsHeaderVm.showPanel('login')
		return
	}

	if (props.selfProfileName) {
		const actionName = props.userInfo.isFollowed ? 'unfollow' : 'follow'
		const PStoken =
			localStorage &&
			localStorage.getItem(`tokenProductSharing_${productSharingData.userId}`)
		const suffix = props.userInfo.isFollowed ? 'unfollow' : 'v2/follow'
		instanceProductSharing
			.post(
				`${productSharingData.apiUrl}/${suffix}`,
				{ username: decodeURIComponent(props.userInfo.username) },
				{ headers: { Authorization: `Bearer ${PStoken}` } },
			)
			.then(({ data: { data } }) => {
				emit('update', {
					name: data.user.username,
					val: data.user.is_followed,
				})
				addMessage(props.text?.msg[actionName].success)
			})
			.catch(err => {
				if (err.response && err.response.status === 401) {
					isHasPSProfile().then(() => updateProfileFollowFlag())
				} else {
					addMessage(props.text?.msg[actionName].fail)
				}
			})
	} else window.location.href = getProductSharingUrl('private-index')
}

function messageCardAction() {
	window.location.href = getProductSharingUrl('profile', {
		name: props.userInfo.username,
	})
}
</script>

<template>
	<div class="bs-product-sharing-message-card" @click="messageCardAction">
		<div class="bs-product-sharing-message-card__avatar">
			<template v-if="isLoading">
				<div
					class="bs-product-sharing-message-card__avatar--loading skeleton"
				></div>
			</template>
			<template v-else>
				<img
					v-if="userAvatar"
					class="bs-product-sharing-message-card__avatar__img"
					:src="userAvatar"
					alt="userAvatar"
				/>
				<img
					v-else
					class="bs-product-sharing-message-card__avatar__img"
					:src="getVueAssetImagePath('avatar_2_48px@2x.png')"
					alt="userAvatar"
				/>
			</template>
		</div>
		<div
			:class="[
				'bs-product-sharing-message-card__info',
				{ 'bs-product-sharing-message-card__info__skeleton': isLoading },
			]"
		>
			<span
				:class="[
					'bs-product-sharing-message-card__info__name',
					{
						'bs-product-sharing-message-card__info__name--loading skeleton':
							isLoading,
					},
				]"
			>
				<template v-if="!isLoading">
					{{ userInfo.username }}
				</template>
			</span>
			<div
				:class="[
					'bs-product-sharing-message-card__info__other',
					{
						'bs-product-sharing-message-card__info__other--loading skeleton':
							isLoading,
					},
				]"
			>
				<span v-if="!isLoading" class="bs-contributions">
					{{ userInfo.totalContributions }}
					{{ text?.contributions }}
				</span>
			</div>
		</div>
		<div v-if="!isLoading" class="bs-product-sharing-message-card__button">
			<BsButtonOutline
				v-if="isNotSelf && userInfo.isFollowed"
				:action="updateProfileFollowFlag"
				:text="text?.button.unFollow"
			></BsButtonOutline>
			<BsButtonNormal
				v-if="isNotSelf && !userInfo.isFollowed"
				:action="updateProfileFollowFlag"
				:text="text?.button.follow"
			></BsButtonNormal>
		</div>
	</div>
</template>
